import type { ComparisonValue } from "@v2/features/infra/analytics/events";
import type {
  AnalyticsQuery,
  MetricName,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { AnalyticsEmptyState } from "@v1/features/analytics/AnalyticsEmptyState";
import { METRICS_REQUIRING_PRORATION } from "@v1/features/analytics/benchmarks";
import {
  CHART_CONFIG_BENCHMARKS,
  CHART_CONFIG_DEFAULT,
  getChartIntervalOptions,
} from "@v1/features/analytics/chart";
import { ConditionallyBlurredComponent } from "@v1/features/analytics/ConditionallyBlurredComponent";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { MetricsOverview } from "@v1/features/analytics/home/MetricsOverview";
import { TopPagesTable } from "@v1/features/analytics/home/TopPagesTable";
import { InsightsCard } from "@v1/features/analytics/insights/InsightsCard";
import { ReploLineChart } from "@v1/features/analytics/ReploLineChart";
import { AnalyticsMainRangeCombobox } from "@v1/features/analytics/selects/AnalyticsTimeRangeCombobox";
import AnalyticsUrlHostCombobox from "@v1/features/analytics/selects/AnalyticsUrlHostCombobox";
import useAnalyticsInsights from "@v1/features/analytics/useAnalyticsInsights";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import { useBenchmarks } from "@v1/features/analytics/useBenchmarks";
import useCurrentAnalyticsTimezone from "@v1/features/analytics/useCurrentAnalyticsTimezone";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import { DEFAULT_METRICS } from "@v2/features/analytics/constants";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import { formatValueToTableCell } from "@v2/features/analytics/utils/formatMetric";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import {
  getRangesFromTimePeriod,
  MAIN_RELATIVE_TIME_FRAMES,
} from "analytics-utils/analytics";
import {
  AppWindow,
  ArrowLeftRight,
  CircleDashed,
  Sparkles,
} from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

const COMPARISON_OPTIONS: {
  label: string;
  value: ComparisonValue;
}[] = [
  { label: "Previous Period", value: "previousPeriod" },
  { label: "Benchmarks", value: "benchmarks" },
];

const Card: React.FC<
  React.PropsWithChildren<{
    title: string;
    icon: React.ReactNode;
    path: string;
  }>
> = ({ children, title, icon, path }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-2 rounded-lg">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <DeprecatedButton variant="secondary" onClick={() => navigate(path)}>
          See all
        </DeprecatedButton>
      </div>
      {children}
    </div>
  );
};

export const AnalyticsHome: React.FC = () => {
  const { dataDisplayType } = useAnalyticsStatus();
  const logEvent = useLogAnalytics();

  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();
  const timeZone = useCurrentAnalyticsTimezone();
  const currency = useCurrentAnalyticsCurrencyCode();

  const { workspace } = useCurrentWorkspaceContext();
  const { project } = useCurrentProjectContext();

  const [comparisonValue, setComparisonValue] =
    React.useState<ComparisonValue>("previousPeriod");

  const workspaceId = workspace?.id ?? "";
  const projectId = project?.id;

  const [activeMetric, setActiveMetric] = React.useState<MetricName>("revenue");

  const { allInsights, activeGroupInsights } =
    useAnalyticsInsights("build-in-replo");

  const handleMetricChange = (metric: MetricName) => {
    setActiveMetric(metric);
  };

  const insightToShow = activeGroupInsights?.[0] ?? allInsights?.[0];

  const ranges = getRangesFromTimePeriod({
    selectedTimePeriod: query.selectedTimePeriod,
    chartInterval: null,
    timeZone,
  }).updatedRanges;

  const { intervalOptions } = getChartIntervalOptions(
    query.selectedTimePeriod,
    timeZone,
  );

  const chartRanges = getRangesFromTimePeriod({
    selectedTimePeriod: query.selectedTimePeriod,
    chartInterval: intervalOptions[0]?.value ?? "day",
    timeZone,
  }).updatedRanges;

  const pageQueryConfig: AnalyticsQuery = {
    ranges,
    sortMetric: "unique_sessions",
    order: "DESC",
    limit: 3,
    offset: 0,
    filters: {
      urlPath: [],
      pageType: "entryPages",
      urlParams: {},
    },
    mode: "groupByUrlPath",
    urlHosts: query.urlHosts,
    metrics: DEFAULT_METRICS,
    isFractional: false,
  };

  const {
    mainRangeResults: mainPageResults,
    isLoading: isPageResultsLoading,
    queryHasNoData: pageQueryHasNoData,
  } = useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: pageQueryConfig,
  });

  const {
    mainRangeResults: mainDomainResults,
    compareAtRangeResults: compareDomainResults,
    isLoading: isDomainResultsLoading,
  } = useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: { ...pageQueryConfig, mode: "groupByDomain" },
  });

  const {
    mainRangeResults: mainDomainChartResults,
    compareAtRangeResults: compareDomainChartResults,
    isLoading: isDomainChartResultsLoading,
    queryHasNoData: chartQueryHasNoData,
    loadedButNoDataForWorkspace: chartLoadedButNoDataForWorkspace,
  } = useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: {
      ...pageQueryConfig,
      mode: "groupByDomain",
      ranges: chartRanges,
    },
  });

  const { benchmarks, industry, aovBracket, benchmarkRangeInDays } =
    useBenchmarks({
      aov: mainDomainResults[0]?.metrics?.average_order_value?.[0] ?? 0,
    });

  /**
   * NOTE (Max, 2025-06-25): Benchmarks are calculated over a 30 day period, so we're not
   * allowing the user to select a range longer than that. That's why, if they come from
   * e.g. the deep dive page & if they had a longer range selected (e.g. 90 days), we override
   * it to only show the last 7 days.
   */
  React.useEffect(() => {
    if (comparisonValue === "benchmarks") {
      const { rangeInDays } = getChartIntervalOptions(
        query.selectedTimePeriod,
        timeZone,
      );

      if (rangeInDays > benchmarkRangeInDays) {
        dispatchAnalyticsQuery({
          type: "updateMainRange",
          payload: "last-7-days",
        });
      }
    }
  }, [
    comparisonValue,
    query.selectedTimePeriod,
    timeZone,
    query,
    dispatchAnalyticsQuery,
    benchmarkRangeInDays,
  ]);

  const insightsPath = Boolean(projectId)
    ? generatePath(routes.shops.analytics.insights, {
        projectId,
      })
    : generatePath(routes.analytics.insights, {
        workspaceId,
      });

  const overviewPath = Boolean(projectId)
    ? generatePath(routes.shops.analytics.overview, {
        projectId,
      })
    : generatePath(routes.analytics.overview, {
        workspaceId,
      });

  const cards = [
    {
      id: "insights",
      title: "Insights",
      icon: <Sparkles size={18} />,
      path: insightsPath,
      children: insightToShow ? (
        <InsightsCard insight={insightToShow} />
      ) : (
        <CardEmptyState
          title="No Insights for now"
          description="We generate weekly tips to boost performance, but need more data to start."
        />
      ),
    },
    {
      id: "top-pages",
      title: "Top Pages",
      icon: <AppWindow size={18} />,
      path: overviewPath,
      children: pageQueryHasNoData ? (
        <CardEmptyState
          title="No Page Views"
          description="We highlight your most visited pages, but need page traffic to do so."
        />
      ) : (
        <TopPagesTable
          isLoading={isPageResultsLoading}
          topPages={mainPageResults.slice(0, 3)}
        />
      ),
    },
  ];

  const primaryChartValues =
    mainDomainChartResults[0]?.metrics[activeMetric] ?? [];

  /**
   * NOTE (Max, 2025-06-25): The benchmark value is for the entire 30 days for which the
   * benchmarks were calculated. When displaying the chart, we need to divide this value
   * by the number of datapoints there are in mainDomainChartResults. This allows the user
   * to visually compare both lines in the chart as the benchmark value is "normalized".
   */
  const benchmarksChartArray = (() => {
    const benchmarkActiveMetricValue = benchmarks?.[activeMetric] ?? 0;

    const chartDataPointsCount = primaryChartValues.length;

    const normalizedBenchmarkValue = METRICS_REQUIRING_PRORATION.includes(
      activeMetric,
    )
      ? benchmarkActiveMetricValue / chartDataPointsCount
      : benchmarkActiveMetricValue;

    return Array.from({
      length: primaryChartValues.length,
    }).map(() => normalizedBenchmarkValue);
  })();

  /**
   * NOTE (Max, 2025-06-25):
   * Benchmarks are calculated over a 30 day period, so we're not allowing the user to select
   * a range longer than that.
   */
  const optionsForBenchmarks = MAIN_RELATIVE_TIME_FRAMES.filter((option) => {
    const { rangeInDays } = getChartIntervalOptions(
      {
        type: "relative",
        value: option.value,
      },
      timeZone,
    );

    return rangeInDays <= benchmarkRangeInDays;
  });

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4">
        <div className="flex flex-row flex-wrap gap-2">
          <AnalyticsMainRangeCombobox
            options={
              comparisonValue === "benchmarks"
                ? optionsForBenchmarks
                : MAIN_RELATIVE_TIME_FRAMES
            }
          />
          <Combobox
            options={COMPARISON_OPTIONS}
            value={comparisonValue}
            onChange={(value: string) => {
              setComparisonValue(value as ComparisonValue);
              logEvent("analytics.home.comparison.change", {
                comparison: value as ComparisonValue,
              });
            }}
            trigger={
              <Combobox.SelectionButton
                startEnhancer={<ArrowLeftRight className="h-4 w-4" />}
                title={
                  COMPARISON_OPTIONS.find(
                    (option) => option.value === comparisonValue,
                  )?.label ?? "Select Comparison"
                }
              />
            }
          />
          <AnalyticsUrlHostCombobox />
        </div>
        {comparisonValue === "benchmarks" && (
          <div className="flex flex-row flex-wrap gap-1 items-center text-xs mt-1 sm:mt-0">
            <span>Comparing</span>
            <Tooltip
              triggerAsChild
              content="Comparing your store with all Replo stores in the same industry and AOV bracket."
            >
              <span className="text-graph-indigo-600 underline decoration-dotted underline-offset-4">
                {industry}
              </span>
            </Tooltip>
            <span>stores with</span>
            {/* TODO (Max, 2025-06-25, INS-868): Convert AOV brackets to the user's currency so we can assign the correct bracket
            to that user if the user's store is not in USD. */}
            {/* eslint-disable-next-line replo/no-tailwind-literal-color */}
            <span className="text-indigo-600">${aovBracket}</span>
            <span>AOV</span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <MetricsOverview
          mainRangeResults={mainDomainResults}
          compareAtRangeResults={compareDomainResults}
          isLoading={isDomainResultsLoading}
          handleMetricChange={handleMetricChange}
          activeMetric={activeMetric}
          comparisonType={comparisonValue}
          benchmarks={benchmarks}
        />
        {isDomainChartResultsLoading ? (
          <Skeleton className="h-[285px] w-full" />
        ) : (
          <HomeContainer>
            {dataDisplayType === "normal" && chartQueryHasNoData ? (
              <AnalyticsEmptyState
                type={
                  chartLoadedButNoDataForWorkspace
                    ? "noTraffic"
                    : "resetFilters"
                }
              />
            ) : (
              <ConditionallyBlurredComponent
                shouldBlur={dataDisplayType === "partialData"}
                blurSize="sm"
                visibleComponent={
                  <AnalyticsEmptyState type="unlockAnalytics" />
                }
                componentToBlur={
                  <ReploLineChart
                    primaryPeriod={primaryChartValues}
                    secondaryPeriod={
                      comparisonValue === "previousPeriod"
                        ? compareDomainChartResults[0]?.metrics[activeMetric] ??
                          []
                        : benchmarksChartArray
                    }
                    ranges={chartRanges}
                    chartInterval="day"
                    timeZone={timeZone}
                    className="h-[250px]"
                    chartConfig={
                      comparisonValue === "benchmarks"
                        ? CHART_CONFIG_BENCHMARKS
                        : CHART_CONFIG_DEFAULT
                    }
                    tooltipValueFormatter={(value) => {
                      return formatValueToTableCell({
                        name: activeMetric,
                        value,
                        currency,
                        isFractional: query.isFractional,
                      });
                    }}
                  />
                }
              />
            )}
          </HomeContainer>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((card) => (
          <Card
            key={card.id}
            title={card.title}
            icon={card.icon}
            path={card.path}
          >
            {card.children}
          </Card>
        ))}
      </div>
    </div>
  );
};

const HomeContainer: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => {
  return (
    <div className={twMerge("border border-border p-4 rounded-lg", className)}>
      {children}
    </div>
  );
};

const CardEmptyState: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => {
  return (
    <HomeContainer>
      <DeprecatedEmptyState
        size="sm"
        variant="noRings"
        title={title}
        description={description}
        layoutClassName="h-[170px]"
        icon={<CircleDashed size={18} />}
      />
    </HomeContainer>
  );
};
